import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, FileSignature, Receipt, CalendarDays,
  GraduationCap, BarChart3, Package, ShieldAlert, DoorOpen, PieChart,
  Sparkles, LogOut, Moon, Sun, Languages, Truck
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "./ui/dropdown-menu";

const NAV = (t) => [
  { to: "/", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/employees", label: t("employees"), icon: Users, testid: "nav-employees" },
  { to: "/documents", label: t("documents"), icon: FileText, testid: "nav-documents" },
  { to: "/contracts", label: t("contracts"), icon: FileSignature, testid: "nav-contracts" },
  { to: "/payroll", label: t("payroll"), icon: Receipt, testid: "nav-payroll" },
  { to: "/leaves", label: t("leaves"), icon: CalendarDays, testid: "nav-leaves" },
  { to: "/trainings", label: t("trainings"), icon: GraduationCap, testid: "nav-trainings" },
  { to: "/performance", label: t("performance"), icon: BarChart3, testid: "nav-performance" },
  { to: "/assets", label: t("assets"), icon: Package, testid: "nav-assets" },
  { to: "/discipline", label: t("discipline"), icon: ShieldAlert, testid: "nav-discipline" },
  { to: "/offboarding", label: t("offboarding"), icon: DoorOpen, testid: "nav-offboarding" },
  { to: "/reports", label: t("reports"), icon: PieChart, testid: "nav-reports" },
  { to: "/ai", label: t("aiTools"), icon: Sparkles, testid: "nav-ai" },
];

export default function AppShell({ children }) {
  const { user, logout, theme, setTheme } = useAuth();
  const { lang, setLang, t } = useI18n();
  const loc = useLocation();
  const navigate = useNavigate();

  const items = NAV(t);
  const initials = (user?.name || user?.email || "U").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex md:flex-col">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <div className="w-9 h-9 rounded-md gradient-primary flex items-center justify-center shadow-md">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold tracking-tight text-base leading-none">LogiHR</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Global</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-primary rounded-r" />}
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border text-xs text-muted-foreground font-mono-brand tracking-wider uppercase">
          v1.0 · {user?.role}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
          <div className="md:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold">LogiHR</span>
          </div>
          <div className="hidden md:block">
            <h2 className="text-lg font-display font-bold tracking-tight">
              {items.find(i => i.to === loc.pathname)?.label || t("dashboard")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon"
              data-testid="btn-toggle-lang"
              onClick={() => setLang(lang === "tr" ? "en" : "tr")}
              className="rounded-full"
              title={t("language")}
            >
              <Languages className="w-4 h-4" />
              <span className="ml-1 text-[11px] font-mono-brand uppercase">{lang}</span>
            </Button>
            <Button
              variant="ghost" size="icon"
              data-testid="btn-toggle-theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="rounded-full"
              title={t("theme")}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-user-menu" className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <div className="text-xs font-medium leading-none">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="w-4 h-4 mr-2" />{t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
